import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { StatCard } from '../ui/StatCard';

/**
 * ANALYTICS — engagement and retention, on top of the overview's three headline numbers.
 *
 * Evan 2026-08-09/10: "tons of KPIs… how many people have RATED venues (not just total ratings)…
 * retention and engagement… since people have joined, how many times on average between ratings,
 * and how long it's been since users have rated places… pick the important ones… do everything as
 * efficiently as possible from a query standpoint."
 *
 * ── TWO QUERIES FOR THE WHOLE PAGE, AND THAT IS THE DESIGN ────────────────────────────────────
 * Every metric here is derived from the same two compact selects — signup timestamps, and one row
 * per rating with its user, venue and time. Nothing else is fetched.
 *
 * The alternative was a count query per KPI, which is how a page like this normally ends up: twenty
 * round trips, twenty spinners, and numbers that disagree with each other because they were measured
 * seconds apart. One fetch means every figure describes the SAME instant, and adding a metric costs
 * nothing at all — it is another reduction over data already in memory, not another request. That is
 * also why the toggles are free: they hide cards, they do not refetch.
 *
 * ⚠️ THE SELECTS ARE DELIBERATELY NARROW. `logs` pulls four columns, not `*` — the row count is what
 * scales here, so every extra column is paid ~1000 times today and far more later. Retired and
 * deleted logs are excluded IN THE QUERY rather than filtered client-side, so they never cross the
 * wire.
 *
 * ⚠️ WHEN THIS STOPS BEING THE RIGHT SHAPE: it computes over every rating in memory, which is
 * correct and fast at ~1k rows and fine into the tens of thousands. Past that, the per-user gap and
 * cohort reductions want a SQL RPC or a materialised view. That is a deliberate trade taken with the
 * numbers as they are, not an oversight — the page is honest about its own scale in the footer.
 */

const PAGE = 1000;

type LogRow = { user_id: string | null; venue_id: string | null; created_at: string };
type ProfileRow = { id: string; created_at: string };

/** Every metric the page can show. `key` is what the toggles persist. */
type MetricKey =
  | 'raters' | 'activation' | 'venuesRated' | 'ratingsPerRater' | 'repeatRate' | 'powerUsers'
  | 'venuesPerRater' | 'revisitRate' | 'medianGap' | 'avgGap' | 'daysSinceLast' | 'dormant'
  | 'active7' | 'active30' | 'ratings7' | 'ratings30' | 'newRaters30' | 'w1' | 'w4';

type Metric = {
  key: MetricKey;
  label: string;
  group: 'Adoption' | 'Depth' | 'Cadence' | 'Recency' | 'Retention';
  value: number | string | null;
  sub?: string;
};

/** Whole days between two instants — floored, so "today" reads as 0 rather than 0.4. */
function daysBetween(a: number, b: number): number {
  return Math.floor((a - b) / 86_400_000);
}
function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((p, q) => p - q);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}
function pct(n: number, d: number): string {
  return d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : '—';
}

export function AnalyticsScreen() {
  const [logs, setLogs] = useState<LogRow[] | null>(null);
  const [profiles, setProfiles] = useState<ProfileRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hidden, setHidden] = useState<Set<MetricKey>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        /**
         * ⚠️ PAGED, because PostgREST caps a response at 1000 rows by default — without this the
         * page would silently under-report the moment the app passes that, and every number would be
         * quietly wrong rather than obviously broken.
         */
        const fetchAll = async <T,>(table: string, cols: string, filter?: (q: any) => any): Promise<T[]> => {
          const out: T[] = [];
          for (let from = 0; ; from += PAGE) {
            let q = supabase.from(table).select(cols).range(from, from + PAGE - 1);
            if (filter) q = filter(q);
            const { data, error } = await q;
            if (error) throw error;
            const rows = (data ?? []) as unknown as T[];
            out.push(...rows);
            if (rows.length < PAGE) break;
          }
          return out;
        };

        const [l, p] = await Promise.all([
          fetchAll<LogRow>('logs', 'user_id,venue_id,created_at', (q) =>
            q.is('retired_at', null).is('deleted_at', null).eq('post_kind', 'rating')),
          fetchAll<ProfileRow>('profiles', 'id,created_at'),
        ]);
        if (cancelled) return;
        setLogs(l);
        setProfiles(p);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load analytics');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const metrics = useMemo((): Metric[] | null => {
    if (!logs || !profiles) return null;
    const now = Date.now();

    /** One pass to bucket by user; everything below reads these rather than re-scanning. */
    const byUser = new Map<string, { times: number[]; venues: Set<string> }>();
    for (const r of logs) {
      if (!r.user_id) continue;
      let e = byUser.get(r.user_id);
      if (!e) { e = { times: [], venues: new Set() }; byUser.set(r.user_id, e); }
      e.times.push(new Date(r.created_at).getTime());
      if (r.venue_id) e.venues.add(r.venue_id);
    }

    const totalUsers = profiles.length;
    const raters = byUser.size;
    const distinctVenues = new Set(logs.map((r) => r.venue_id).filter(Boolean)).size;

    const gaps: number[] = [];
    const sinceLast: number[] = [];
    let repeat = 0, power = 0, active7 = 0, active30 = 0, dormant = 0, revisiters = 0;
    let venuesPerRaterSum = 0;

    byUser.forEach((e) => {
      const t = e.times.sort((a, b) => a - b);
      if (t.length >= 2) {
        repeat++;
        for (let i = 1; i < t.length; i++) gaps.push(daysBetween(t[i], t[i - 1]));
      }
      if (t.length >= 5) power++;
      /** More ratings than distinct venues means at least one venue was rated twice. */
      if (t.length > e.venues.size) revisiters++;
      venuesPerRaterSum += e.venues.size;
      const last = t[t.length - 1];
      const d = daysBetween(now, last);
      sinceLast.push(d);
      if (d <= 7) active7++;
      if (d <= 30) active30++;
      if (d > 30) dormant++;
    });

    const ratings7 = logs.filter((r) => daysBetween(now, new Date(r.created_at).getTime()) <= 7).length;
    const ratings30 = logs.filter((r) => daysBetween(now, new Date(r.created_at).getTime()) <= 30).length;

    /**
     * ⚠️ RETENTION IS DEFINED AS "RATED IN WEEK N AFTER SIGNUP", not "opened the app". This product
     * is ratings — a user who opens it and rates nothing has not been retained by any measure that
     * matters here — and it is the only definition these two tables can support honestly.
     *
     * ⚠️ THE DENOMINATOR EXCLUDES USERS WHO HAVE NOT HAD THE CHANCE. Someone who joined three days
     * ago cannot have a week-4 outcome, and counting them as a miss drags the figure down every time
     * signups grow. Only cohorts old enough to have completed the window are counted.
     */
    const firstRating = new Map<string, number>();
    byUser.forEach((e, uid) => firstRating.set(uid, e.times[0]));
    const weekN = (n: number) => {
      let eligible = 0, retained = 0;
      for (const p of profiles) {
        const joined = new Date(p.created_at).getTime();
        const ageDays = daysBetween(now, joined);
        if (ageDays < n * 7) continue;
        eligible++;
        const e = byUser.get(p.id);
        if (!e) continue;
        const lo = joined + (n - 1) * 7 * 86_400_000;
        const hi = joined + n * 7 * 86_400_000;
        if (e.times.some((t) => t >= lo && t < hi)) retained++;
      }
      return { eligible, retained };
    };
    const w1 = weekN(1);
    const w4 = weekN(4);

    let newRaters30 = 0;
    firstRating.forEach((t) => { if (daysBetween(now, t) <= 30) newRaters30++; });
    const medGap = median(gaps);
    const avgGap = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;
    const medSince = median(sinceLast);

    return [
      { key:'raters', group:'Adoption', label:'People who have rated', value:raters,
        sub:`of ${totalUsers.toLocaleString()} accounts` },
      { key:'activation', group:'Adoption', label:'Activation rate', value:pct(raters, totalUsers),
        sub:'signed up → rated at least once' },
      { key:'venuesRated', group:'Adoption', label:'Venues with a rating', value:distinctVenues },
      { key:'newRaters30', group:'Adoption', label:'First-time raters (30d)', value:newRaters30,
        sub:'users whose FIRST rating was in the last 30 days' },

      { key:'ratingsPerRater', group:'Depth', label:'Ratings per rater',
        value: raters ? Math.round((logs.length / raters) * 10) / 10 : null, sub:'mean' },
      { key:'venuesPerRater', group:'Depth', label:'Distinct venues per rater',
        value: raters ? Math.round((venuesPerRaterSum / raters) * 10) / 10 : null, sub:'mean' },
      { key:'repeatRate', group:'Depth', label:'Rated more than once', value:pct(repeat, raters),
        sub:`${repeat.toLocaleString()} of ${raters.toLocaleString()} raters` },
      { key:'powerUsers', group:'Depth', label:'5+ ratings', value:power,
        sub: raters ? `${pct(power, raters)} of raters` : undefined },
      { key:'revisitRate', group:'Depth', label:'Rated a venue twice', value:pct(revisiters, raters),
        sub:'came back to somewhere they had already rated' },

      { key:'medianGap', group:'Cadence', label:'Median days between ratings',
        value: medGap == null ? null : `${medGap}d`, sub:'across every consecutive pair' },
      { key:'avgGap', group:'Cadence', label:'Mean days between ratings',
        value: avgGap == null ? null : `${Math.round(avgGap * 10) / 10}d`,
        sub:'mean is dragged by long lapses — read with the median' },
      { key:'ratings7', group:'Cadence', label:'Ratings (7d)', value:ratings7 },
      { key:'ratings30', group:'Cadence', label:'Ratings (30d)', value:ratings30 },

      { key:'daysSinceLast', group:'Recency', label:'Median days since last rating',
        value: medSince == null ? null : `${medSince}d`, sub:'per rater' },
      { key:'active7', group:'Recency', label:'Rated in last 7 days', value:active7,
        sub: raters ? `${pct(active7, raters)} of raters` : undefined },
      { key:'active30', group:'Recency', label:'Rated in last 30 days', value:active30,
        sub: raters ? `${pct(active30, raters)} of raters` : undefined },
      { key:'dormant', group:'Recency', label:'Dormant raters', value:dormant,
        sub:'rated before, nothing in 30 days' },

      { key:'w1', group:'Retention', label:'Week 1 retention', value:pct(w1.retained, w1.eligible),
        sub:`rated in their first week · ${w1.eligible.toLocaleString()} eligible` },
      { key:'w4', group:'Retention', label:'Week 4 retention', value:pct(w4.retained, w4.eligible),
        sub:`rated in week 4 · ${w4.eligible.toLocaleString()} eligible` },
    ];
  }, [logs, profiles]);

  const groups: Metric['group'][] = ['Adoption', 'Depth', 'Cadence', 'Recency', 'Retention'];
  const toggle = (k: MetricKey) =>
    setHidden((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });

  return (
    <div className="admin-page mx-auto max-w-5xl">
      <h1 className="admin-h1">Analytics</h1>
      <p className="admin-sub mb-4">
        Engagement and retention across every rating. Toggle any metric off to focus the view.
      </p>

      {err ? <div className="admin-card text-danger">{err}</div> : null}
      {!metrics && !err ? <div className="admin-card text-mutedDim">Loading…</div> : null}

      {metrics ? (
        <>
          <div className="admin-card mb-4">
            <div className="admin-eyebrow mb-2">Show</div>
            <div className="flex flex-wrap gap-1.5">
              {metrics.map((m) => {
                const on = !hidden.has(m.key);
                return (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => toggle(m.key)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                      on
                        ? 'border-accent/50 bg-accent/15 text-textPrimary'
                        : 'border-border text-mutedDim hover:text-textPrimary'
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {groups.map((g) => {
            const shown = metrics.filter((m) => m.group === g && !hidden.has(m.key));
            if (!shown.length) return null;
            return (
              <section key={g} className="mb-5">
                <div className="admin-eyebrow mb-2">{g}</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {shown.map((m) => (
                    <StatCard key={m.key} label={m.label} value={m.value} sub={m.sub} />
                  ))}
                </div>
              </section>
            );
          })}

          <p className="text-[11px] leading-relaxed text-mutedDim">
            Every figure above comes from one pass over {logs?.length.toLocaleString()} ratings and{' '}
            {profiles?.length.toLocaleString()} accounts, fetched once — so they all describe the same
            instant rather than disagreeing by however long a series of queries took. Retention counts
            a user as retained if they <em>rated</em> in that week, and excludes anyone who has not
            been signed up long enough to have had the chance.
          </p>
        </>
      ) : null}
    </div>
  );
}
